import type React from 'react';
import {
  forwardRef,
  useId,
  useState,
  type HTMLAttributes,
  type InputHTMLAttributes,
} from 'react';
import { Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { Button, cn } from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import type { ControlledTextFormFieldProps } from 'types/common/form-fields';
import { CustomField, FieldLabel, FieldMessage, MuteLabel } from './chrome';
import { DATEPICKER_SCOPE, FIELD_INPUT, FIELD_INPUT_INVALID } from './styles';

type CalendarInputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
};

/**
 * The text box react-datepicker opens its calendar from. It clones this with
 * its own value and handlers, which all have to reach the `<input>`.
 */
const CalendarInput = forwardRef<HTMLInputElement, CalendarInputProps>(
  ({ className, invalid, ...rest }, ref) => (
    <span className="sk:relative sk:flex sk:w-full sk:items-center">
      <input
        {...rest}
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          FIELD_INPUT,
          'sk:pr-8!',
          invalid && FIELD_INPUT_INVALID,
          className,
          'sk:m-0!',
        )}
      />
      <CalendarBlankIcon
        size={14}
        aria-hidden
        className="sk:pointer-events-none sk:absolute sk:right-3 sk:text-fg-tertiary"
      />
    </span>
  ),
);
CalendarInput.displayName = 'CalendarInput';

interface IDateFormFieldProps extends ControlledTextFormFieldProps {
  minDate?: Date;
  maxDate?: Date;
}

export function DateFormField({
  required,
  error,
  label,
  hint,
  control,
  name,
  width,
  defaultValue = '',
  inline,
  hideLabel = false,
  helpText,
  helpPosition = 'top center',
  fieldClass = '',
  ...rest
}: IDateFormFieldProps) {
  const id = useId();
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          className={cn(DATEPICKER_SCOPE, fieldClass)}
          width={width}
          inline={inline || false}
        >
          {!hideLabel && (
            <FieldLabel
              label={label}
              required={required}
              helpText={helpText}
              helpPosition={helpPosition}
              htmlFor={id}
            />
          )}

          <DatePicker
            id={id}
            formatWeekDay={nameOfDay => nameOfDay.substring(0, 3)}
            selected={value}
            onChange={date => {
              onChange(date);
            }}
            placeholderText={typeof label === 'string' ? label : undefined}
            customInput={
              <CalendarInput data-field-value={name} invalid={!!error} />
            }
            dateFormat={DateFormat.datepicker}
            disabledKeyboardNavigation
            {...rest}
          />
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function RangeDateFormField({
  required,
  error,
  label,
  hint,
  control,
  name,
  width,
  defaultValue = '',
  hideLabel = false,
  fieldClass,
  resetUTC = true,
  ...rest
}) {
  const id = useId();
  const getDateValue = (value: string | number | Date) => {
    const updated = new Date(value);
    if (resetUTC) {
      return new Date(updated.getTime() + updated.getTimezoneOffset() * 60000);
    }
    return updated;
  };
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          className={cn(
            DATEPICKER_SCOPE,
            'sk:flex sk:flex-col',
            /* The hint gives way to the calendar while it is open. */
            'sk:focus-within:[&_.hint]:hidden',
            fieldClass,
          )}
          width={width}
        >
          {!hideLabel && (
            <FieldLabel label={label} required={required} htmlFor={id} />
          )}
          <DatePicker
            id={id}
            formatWeekDay={nameOfDay => nameOfDay.substring(0, 3)}
            startDate={value ? value[0] && getDateValue(value[0]) : null}
            endDate={value ? value[1] && getDateValue(value[1]) : null}
            onChange={date => {
              onChange(date);
            }}
            placeholderText={typeof label === 'string' ? label : undefined}
            customInput={
              <CalendarInput data-field-value={name} invalid={!!error} />
            }
            dateFormat={DateFormat.datepicker}
            disabledKeyboardNavigation
            {...rest}
          />
          {hint?.[0] && (
            <FieldMessage className="hint">{hint[0].message}</FieldMessage>
          )}
          {hint?.[1] && (
            <FieldMessage className="hint">{hint[1].message}</FieldMessage>
          )}
          {hint?.message && (
            <FieldMessage className="hint">{hint?.message}</FieldMessage>
          )}
        </CustomField>
      )}
    />
  );
}

type CustomDateInputProps = {
  date?: string | Date;
  label?: string | React.ReactElement;
} & InputHTMLAttributes<HTMLInputElement>;

export function CustomDateInput({
  date = '',
  label = '',
  className,
  value: _value,
  ...rest
}: CustomDateInputProps) {
  const dateValue = DateUtility.formatDate(date, DateFormat.date);
  return (
    <div className="sk:flex sk:flex-col">
      {label && <MuteLabel className="sk:mb-1">{label}</MuteLabel>}
      <input
        readOnly
        {...rest}
        value={dateValue}
        className={cn(FIELD_INPUT, className, 'sk:m-0!')}
      />
    </div>
  );
}

type DivProps = HTMLAttributes<HTMLDivElement>;

/** Frames an inline react-datepicker calendar in the kit's look. */
export const DatepickerContainer = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn('sk:flex sk:flex-col', DATEPICKER_SCOPE, className)}
    />
  ),
);
DatepickerContainer.displayName = 'DatepickerContainer';

export const DateRangeInputContainer = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'sk:mb-4 sk:flex sk:items-center sk:justify-between sk:gap-4',
        className,
      )}
    />
  ),
);
DateRangeInputContainer.displayName = 'DateRangeInputContainer';

export function DateRangePicker({
  isInvalidDate = false,
  dateRangeValue = [],
  onApply,
}) {
  const [dpDateRange, setDpDateRange] = useState([
    dateRangeValue[0] || new Date(),
    dateRangeValue[1] || new Date(),
  ]);
  const [isOpen, setIsOpen] = useState(false);

  const handleApply = () => {
    onApply(dpDateRange);
    setIsOpen(false);
  };

  return (
    <DatepickerContainer className="sk:mt-2 sk:w-full">
      <DateRangeInputContainer onClick={() => setIsOpen(true)}>
        <CustomDateInput label="Start" date={dpDateRange[0]} />
        <CustomDateInput label="End" date={dpDateRange[1]} />
      </DateRangeInputContainer>
      {isOpen && (
        <DatePicker
          selectsRange
          startDate={dpDateRange[0]}
          endDate={dpDateRange[1]}
          dateFormat={DateFormat.date}
          maxDate={DateUtility.getTodayDate()}
          onChange={update => setDpDateRange(update)}
          inline
        >
          <div className="sk:mt-2 sk:mb-3 sk:flex sk:w-full sk:justify-around sk:gap-2 sk:px-2">
            <Button
              variant="pill"
              className="sk:flex-1"
              onPress={() => {
                setIsOpen(false);
                setDpDateRange(dateRangeValue);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="sk:flex-1"
              onPress={handleApply}
            >
              Apply
            </Button>
          </div>
        </DatePicker>
      )}
      {isInvalidDate && <FieldMessage>Invalid date range</FieldMessage>}
    </DatepickerContainer>
  );
}
